import React, { useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { diffLines } from 'diff';
import type { Proposal } from '../pages/KnowledgePage';
import { API_BASE_URL, authFetch } from '../config/api';

const baseUrl = API_BASE_URL;

interface Props {
  proposal: Proposal;
  onApprove: () => void;
  onReject: () => void;
  onSaveEdit: (content: string) => void;
  isApproving: boolean;
  isRejecting: boolean;
}

interface VaultFile {
  file_path: string;
  title: string;
}

type ViewMode = 'rendered' | 'diff' | 'raw';

/** Split markdown into frontmatter + body so we can render the body nicely. */
function splitFrontmatter(md: string): { frontmatter: string; body: string } {
  const match = md.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
  if (match) return { frontmatter: match[1], body: match[2] };
  return { frontmatter: '', body: md };
}

/** Compute a unified diff view with added/removed/unchanged lines. */
function computeDiff(
  oldText: string | null,
  newText: string,
): { type: 'added' | 'removed' | 'unchanged'; line: string }[] {
  if (!oldText) {
    return newText.split('\n').map(line => ({ type: 'added', line }));
  }
  const changes = diffLines(oldText, newText);
  const lines: { type: 'added' | 'removed' | 'unchanged'; line: string }[] = [];
  for (const part of changes) {
    const partLines = part.value.replace(/\n$/, '').split('\n');
    const type = part.added ? 'added' : part.removed ? 'removed' : 'unchanged';
    for (const line of partLines) {
      lines.push({ type, line });
    }
  }
  return lines;
}

async function fetchVaultFiles(): Promise<VaultFile[]> {
  const res = await authFetch(`${baseUrl}/api/knowledge/vault/files`);
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as { ok: boolean; files: VaultFile[] };
  return data.files;
}

async function combineWithNote(id: number, filePath: string): Promise<void> {
  const res = await authFetch(`${baseUrl}/api/knowledge/staging/${id}/combine`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ file_path: filePath }),
  });
  if (!res.ok) throw new Error(await res.text());
}

export default function DiffView({
  proposal,
  onApprove,
  onReject,
  onSaveEdit,
  isApproving,
  isRejecting,
}: Props) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(proposal.proposed_content);
  const [viewMode, setViewMode] = useState<ViewMode>('rendered');
  const [showLinkPicker, setShowLinkPicker] = useState(false);
  const [linkSearch, setLinkSearch] = useState('');
  const [combining, setCombining] = useState(false);
  const [extracting, setExtracting] = useState(false);

  // Reset state when proposal changes
  const [prevId, setPrevId] = useState(proposal.id);
  if (proposal.id !== prevId) {
    setPrevId(proposal.id);
    setEditing(false);
    setEditContent(proposal.proposed_content);
    setViewMode('rendered');
    setShowLinkPicker(false);
    setLinkSearch('');
  }

  const isPending = proposal.status === 'pending' || proposal.status === 'edited';

  const proposed = splitFrontmatter(proposal.proposed_content);
  const current = proposal.current_content
    ? splitFrontmatter(proposal.current_content)
    : null;

  const diffLines_ = useMemo(
    () => computeDiff(proposal.current_content, proposal.proposed_content),
    [proposal.current_content, proposal.proposed_content],
  );

  // Fetch vault files only when link picker is open
  const { data: vaultFiles } = useQuery<VaultFile[]>(
    ['vaultFiles'],
    fetchVaultFiles,
    { enabled: showLinkPicker, staleTime: 60000 },
  );

  const filteredFiles = vaultFiles?.filter(f =>
    f.title.toLowerCase().includes(linkSearch.toLowerCase()) ||
    f.file_path.toLowerCase().includes(linkSearch.toLowerCase())
  );

  async function handleExtract() {
    const isHazard = proposal.tags.some(t =>
      t.toLowerCase() === 'hazard' || t.toLowerCase() === 'hazards'
    ) || proposal.target_file_path.toLowerCase().includes('hazard');
    const folder = isHazard ? '2 Areas/Food Safety/Hazards' : '2 Areas/Food Safety';
    const suggested = `${folder}/${proposal.title}.md`;
    const input = window.prompt('New file path in vault (leave blank for default):', suggested);
    if (input === null) return; // cancelled
    setExtracting(true);
    try {
      const res = await authFetch(`${baseUrl}/api/knowledge/staging/${proposal.id}/extract`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_file_path: input.trim() || suggested }),
      });
      if (!res.ok) throw new Error(await res.text());
      queryClient.invalidateQueries(['knowledgeStaging']);
    } catch (e) {
      alert('Failed to extract: ' + (e as Error).message);
    } finally {
      setExtracting(false);
    }
  }

  async function handleCombine(filePath: string) {
    setCombining(true);
    try {
      await combineWithNote(proposal.id, filePath);
      queryClient.invalidateQueries(['knowledgeStaging']);
      setShowLinkPicker(false);
      setLinkSearch('');
    } catch (e) {
      alert('Failed to combine: ' + (e as Error).message);
    } finally {
      setCombining(false);
    }
  }

  return (
    <div className="diff-view">
      {/* Header */}
      <div className="diff-view__header">
        <div>
          <h3>{proposal.title}</h3>
          <span className="diff-view__path">{proposal.target_file_path}</span>
          {proposal.tags.length > 0 && (
            <div className="diff-view__tags">
              {proposal.tags.map(t => (
                <span key={t} className="diff-view__tag">{t}</span>
              ))}
            </div>
          )}
        </div>
        <div className="diff-view__controls">
          {/* View mode toggle */}
          <div className="diff-view__mode-toggle">
            {(['rendered', 'diff', 'raw'] as ViewMode[]).map(m => (
              <button
                key={m}
                className={`diff-mode-btn ${viewMode === m ? 'diff-mode-btn--active' : ''}`}
                onClick={() => setViewMode(m)}
              >
                {m === 'rendered' ? 'Preview' : m === 'diff' ? 'Changes' : 'Raw'}
              </button>
            ))}
          </div>
          {isPending && (
            <div className="diff-view__actions">
              <button
                className="btn btn--edit"
                onClick={() => setShowLinkPicker(v => !v)}
                disabled={combining || extracting}
              >
                {combining ? 'Combining...' : showLinkPicker ? 'Cancel' : 'Combine'}
              </button>
              <button
                className="btn btn--edit"
                onClick={handleExtract}
                disabled={extracting || combining}
                title={proposal.current_content ? 'Extract new content into a separate standalone note' : 'Save as a new file at a different path'}
              >
                {extracting ? 'Extracting...' : 'New File'}
              </button>
              <button
                className="btn btn--edit"
                onClick={() => {
                  if (editing) {
                    onSaveEdit(editContent);
                    setEditing(false);
                  } else {
                    setEditContent(proposal.proposed_content);
                    setEditing(true);
                  }
                }}
              >
                {editing ? 'Save Edit' : 'Edit'}
              </button>
              <button
                className="btn btn--reject"
                onClick={onReject}
                disabled={isRejecting}
              >
                {isRejecting ? 'Rejecting...' : 'Reject'}
              </button>
              <button
                className="btn btn--approve"
                onClick={onApprove}
                disabled={isApproving}
              >
                {isApproving ? 'Writing...' : 'Approve & Write'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Link picker */}
      {showLinkPicker && (
        <div className="link-picker">
          <div className="link-picker__header">
            <span className="link-picker__title">Combine with existing Obsidian note</span>
            <input
              className="link-picker__search"
              type="text"
              placeholder="Search notes..."
              value={linkSearch}
              onChange={e => setLinkSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="link-picker__list">
            {!vaultFiles && <div className="link-picker__loading">Loading vault files...</div>}
            {filteredFiles?.map(f => (
              <button
                key={f.file_path}
                className="link-picker__item"
                onClick={() => handleCombine(f.file_path)}
                disabled={combining}
              >
                <span className="link-picker__item-title">{f.title}</span>
                <span className="link-picker__item-path">{f.file_path}</span>
              </button>
            ))}
            {filteredFiles?.length === 0 && (
              <div className="link-picker__empty">No matching notes found</div>
            )}
          </div>
        </div>
      )}

      {/* Content area */}
      {editing ? (
        <div className="diff-panel__content">
          <textarea
            className="diff-panel__editor"
            value={editContent}
            onChange={e => setEditContent(e.target.value)}
          />
        </div>
      ) : viewMode === 'rendered' ? (
        <div className="diff-view__panels">
          {/* Left: current */}
          <div className="diff-panel">
            <div className="diff-panel__label">
              {current ? 'Current Note' : 'No existing note'}
            </div>
            <div className="diff-panel__content">
              {current ? (
                <>
                  <div className="diff-frontmatter">
                    <pre>{current.frontmatter}</pre>
                  </div>
                  <div className="diff-markdown">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {current.body}
                    </ReactMarkdown>
                  </div>
                </>
              ) : (
                <p className="diff-panel__placeholder">
                  This is a new note — no existing content in Obsidian.
                </p>
              )}
            </div>
          </div>

          {/* Right: proposed */}
          <div className="diff-panel diff-panel--proposed">
            <div className="diff-panel__label">Proposed Content</div>
            <div className="diff-panel__content">
              <div className="diff-frontmatter">
                <pre>{proposed.frontmatter}</pre>
              </div>
              <div className="diff-markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {proposed.body}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      ) : viewMode === 'diff' ? (
        <div className="diff-unified">
          <div className="diff-panel__label">
            {proposal.current_content ? 'Changes (current → proposed)' : 'New content (all added)'}
          </div>
          <div className="diff-unified__content">
            {diffLines_.map((line, i) => (
              <div
                key={i}
                className={`diff-line diff-line--${line.type}`}
              >
                <span className="diff-line__marker">
                  {line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' '}
                </span>
                <span className="diff-line__text">{line.line}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* Raw view */
        <div className="diff-view__panels">
          <div className="diff-panel">
            <div className="diff-panel__label">
              {proposal.current_content ? 'Current Note' : 'No existing note'}
            </div>
            <div className="diff-panel__content">
              {proposal.current_content ? (
                <pre>{proposal.current_content}</pre>
              ) : (
                <p className="diff-panel__placeholder">
                  This is a new note — no existing content in Obsidian.
                </p>
              )}
            </div>
          </div>
          <div className="diff-panel diff-panel--proposed">
            <div className="diff-panel__label">Proposed Content</div>
            <div className="diff-panel__content">
              <pre>{proposal.proposed_content}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
